/**
 * Centralized Application Configuration
 * 
 * This file centralizes all hardcoded values and makes them configurable
 * through environment variables. Supports multi-company deployments.
 * 
 * All configuration values can be overridden via environment variables
 * following the pattern: REACT_APP_<CATEGORY>_<SETTING>
 */

import { firebaseConfig as resolvedFirebaseConfig } from '../firebase/config';

// Get current Firebase project ID from firebase/config (single source of truth; matches Auth/Firestore/Functions)
const getFirebaseProjectId = () => {
  return resolvedFirebaseConfig?.projectId || process.env.REACT_APP_FIREBASE_PROJECT_ID || '';
};

// Get current Firebase region from environment
const getFirebaseRegion = () => {
  return process.env.REACT_APP_FIREBASE_REGION || 'us-central1';
};

/**
 * Application Version
 * Single source of truth for app version. Update here when releasing.
 * Format: X.YY (e.g. 1.03, 1.04, 2.01)
 */
export const APP_VERSION = process.env.REACT_APP_VERSION || '2.0';

/**
 * Company Configuration
 * Company-specific settings that can vary per deployment
 */
export const companyConfig = {
  // Company name/branding
  name: process.env.REACT_APP_COMPANY_NAME || 'Plutoo',
  displayName: process.env.REACT_APP_COMPANY_DISPLAY_NAME || 'Plutoo Dealer',
  supportName: process.env.REACT_APP_SUPPORT_NAME || 'Plutoo Dealer Support',
  
  // Admin email domain (users with this domain get admin access)
  adminEmailDomain: process.env.REACT_APP_ADMIN_EMAIL_DOMAIN || '@plutoo.co.uk',
  
  // Support email
  supportEmail: process.env.REACT_APP_SUPPORT_EMAIL || 'support@plutoo.co.uk',
  
  // Logo fallback name (for default logo component)
  logoName: process.env.REACT_APP_LOGO_NAME || 'Plutoo',
};

/**
 * Firebase Configuration
 * Firebase-related settings
 */
export const firebaseConfig = {
  // Firebase project ID (from environment)
  projectId: getFirebaseProjectId(),
  
  // Firebase region for Cloud Functions
  region: getFirebaseRegion(),
  
  // Firebase console URL template (for error messages)
  consoleUrlTemplate: process.env.REACT_APP_FIREBASE_CONSOLE_URL_TEMPLATE || 
    'https://console.firebase.google.com/project/{projectId}/settings/general',
  
  // Get Firebase console URL for current project
  getConsoleUrl: () => {
    const projectId = getFirebaseProjectId();
    return firebaseConfig.consoleUrlTemplate.replace('{projectId}', projectId);
  },
  
  // Get Cloud Functions base URL (1st gen format)
  getFunctionsBaseUrl: () => {
    const region = getFirebaseRegion();
    const projectId = getFirebaseProjectId();
    return `https://${region}-${projectId}.cloudfunctions.net`;
  },

  // Optional per-function URLs for Gen 2 (Cloud Run). If set, used instead of base + name.
  getFunctionUrl: (functionName) => {
    const overrides = {
      dvlaLookup: process.env.REACT_APP_DVLA_LOOKUP_URL,
      motCheck: process.env.REACT_APP_MOT_CHECK_URL,
      motWebsiteLookup: process.env.REACT_APP_MOT_WEBSITE_LOOKUP_URL,
      sendWhatsApp: process.env.REACT_APP_SEND_WHATSAPP_URL,
      getWhatsAppTemplates: process.env.REACT_APP_GET_WHATSAPP_TEMPLATES_URL,
      listAuthUsers: process.env.REACT_APP_LIST_AUTH_USERS_URL,
      sendPasswordReset: process.env.REACT_APP_SEND_PASSWORD_RESET_URL,
      sendQuoteToCustomer: process.env.REACT_APP_SEND_QUOTE_TO_CUSTOMER_URL,
      sendVehicleMediaEmail: process.env.REACT_APP_SEND_VEHICLE_MEDIA_EMAIL_URL,
      createUser: process.env.REACT_APP_CREATE_USER_URL,
      updateUser: process.env.REACT_APP_UPDATE_USER_URL,
      deleteUser: process.env.REACT_APP_DELETE_USER_URL,
      createCheckoutSession: process.env.REACT_APP_CREATE_CHECKOUT_SESSION_URL,
      createPortalSession: process.env.REACT_APP_CREATE_PORTAL_SESSION_URL,
      getSubscriptionStatus: process.env.REACT_APP_GET_SUBSCRIPTION_STATUS_URL,
      verifyCheckoutSession: process.env.REACT_APP_VERIFY_CHECKOUT_SESSION_URL,
      createTrialSubscription: process.env.REACT_APP_CREATE_TRIAL_SUBSCRIPTION_URL,
      migrateLocalStorageTrial: process.env.REACT_APP_MIGRATE_LOCAL_STORAGE_TRIAL_URL,
      validateVehicleCreation: process.env.REACT_APP_VALIDATE_VEHICLE_CREATION_URL,
      modifySubscription: process.env.REACT_APP_MODIFY_SUBSCRIPTION_URL,
    };
    const url = overrides[functionName];
    if (url) return url;
    const base = firebaseConfig.getFunctionsBaseUrl();
    return `${base}/${functionName}`;
  },
};

/**
 * API Configuration
 * External API endpoints and settings
 */
// Normalise Jupiter URL (no trailing slash) so supportService can safely append /supportTicketsCreate etc.
const rawJupiterUrl = process.env.REACT_APP_JUPITER_API_URL || 'http://localhost:5001/api';
const supportApiUrlNormalised = rawJupiterUrl.replace(/\/+$/, '');

export const apiConfig = {
  // Support/Jupiter API
  supportApiUrl: supportApiUrlNormalised,
  supportApiKey: (process.env.REACT_APP_JUPITER_API_KEY || '').trim(),
  
  // DVLA API
  dvlaApiUrl: process.env.REACT_APP_DVLA_API_URL || 
    'https://driver-vehicle-licensing.api.gov.uk/vehicle-enquiry/v1/vehicles',
  
  // MOT API
  motApiUrl: process.env.REACT_APP_MOT_API_URL || 
    'https://beta.check-mot.service.gov.uk/tradeapi/v1/mot-tests',
  
  // External services
  googleFaviconApi: process.env.REACT_APP_GOOGLE_FAVICON_API || 
    'https://www.google.com/s2/favicons?domain={domain}&sz=64',
  
  // Request timeouts (in milliseconds)
  timeout: {
    default: parseInt(process.env.REACT_APP_API_TIMEOUT || '30000', 10),
    whatsapp: parseInt(process.env.REACT_APP_WHATSAPP_TIMEOUT || '30000', 10),
    support: parseInt(process.env.REACT_APP_SUPPORT_TIMEOUT || '30000', 10),
  },
};

/**
 * Stripe Configuration
 * Stripe payment settings
 */
export const stripeConfig = {
  // Stripe publishable key
  publishableKey: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '',
  
  // Stripe price IDs
  priceIds: {
    lite: process.env.REACT_APP_STRIPE_PRICE_ID_LITE || '',
    premium: process.env.REACT_APP_STRIPE_PRICE_ID_PREMIUM || '',
  },
  
  // Subscription pricing (in pence)
  pricing: {
    lite: parseInt(process.env.REACT_APP_STRIPE_PRICE_LITE_PENCE || '19999', 10), // £199.99
    premium: parseInt(process.env.REACT_APP_STRIPE_PRICE_PREMIUM_PENCE || '29999', 10), // £299.99
  },
};

/**
 * Adyen Configuration
 * Adyen payment settings
 */
export const adyenConfig = {
  environment: process.env.REACT_APP_ADYEN_ENVIRONMENT || 'test',
  clientKey: process.env.REACT_APP_ADYEN_CLIENT_KEY || '',
  merchantAccount: process.env.REACT_APP_ADYEN_MERCHANT_ACCOUNT || '',
  
  // Pricing (in pence)
  pricing: {
    lite: parseInt(process.env.REACT_APP_ADYEN_PRICE_LITE_PENCE || '19999', 10),
    premium: parseInt(process.env.REACT_APP_ADYEN_PRICE_PREMIUM_PENCE || '29999', 10),
  },
};

/**
 * Locale Configuration
 * Currency, language, and regional settings
 */
export const localeConfig = {
  // Currency
  currency: process.env.REACT_APP_CURRENCY || 'GBP',
  currencySymbol: process.env.REACT_APP_CURRENCY_SYMBOL || '£',
  
  // Country code for phone numbers (default: UK)
  defaultCountryCode: process.env.REACT_APP_DEFAULT_COUNTRY_CODE || '+44',
  defaultCountry: process.env.REACT_APP_DEFAULT_COUNTRY || 'UK',
  
  // Date/time format
  dateFormat: process.env.REACT_APP_DATE_FORMAT || 'DD/MM/YYYY',
  timeFormat: process.env.REACT_APP_TIME_FORMAT || 'HH:mm',
};

/**
 * Platform URLs
 * External platform listing URLs (for vehicle listings)
 */
export const platformUrls = {
  autotrader: process.env.REACT_APP_PLATFORM_AUTOTRADER_URL || 'https://www.autotrader.co.uk/...',
  motorway: process.env.REACT_APP_PLATFORM_MOTORWAY_URL || 'https://motorway.co.uk/...',
  bca: process.env.REACT_APP_PLATFORM_BCA_URL || 'https://www.bca-europe.com/...',
  facebook: process.env.REACT_APP_PLATFORM_FACEBOOK_URL || 'https://www.facebook.com/marketplace/...',
  ebay: process.env.REACT_APP_PLATFORM_EBAY_URL || 'https://www.ebay.co.uk/...',
  
  // Default placeholder
  placeholder: process.env.REACT_APP_PLATFORM_PLACEHOLDER || 'https://example.com',
};

/**
 * UI/UX Configuration
 * User interface strings and messages
 */
export const uiConfig = {
  // Welcome messages
  welcomeMessage: process.env.REACT_APP_WELCOME_MESSAGE || 
    `Hello! Welcome to ${companyConfig.supportName}. How can I help you today?`,
  
  // Support ticket confirmation message
  ticketConfirmationMessage: (ticketId) => 
    process.env.REACT_APP_TICKET_CONFIRMATION_MESSAGE || 
    `Thank you for your message. Your support ticket (#${ticketId}) has been created. A ${companyConfig.supportName} representative will get back to you shortly.`,
  
  // Error messages
  errorMessages: {
    support: process.env.REACT_APP_ERROR_SUPPORT || 
      `Sorry, we encountered an error. Please try again or contact us directly at ${companyConfig.supportEmail}`,
    generic: process.env.REACT_APP_ERROR_GENERIC || 'An error occurred. Please try again.',
  },
  
  // Powered by text
  poweredBy: process.env.REACT_APP_POWERED_BY || `Powered by ${companyConfig.supportName}`,
};

/**
 * WhatsApp Configuration
 * WhatsApp service settings
 */
export const whatsappConfig = {
  // Get WhatsApp function URL (uses Gen 2 override when set)
  getFunctionUrl: () => firebaseConfig.getFunctionUrl('sendWhatsApp'),
  
  // Delay between messages in broadcast (milliseconds)
  broadcastDelay: parseInt(process.env.REACT_APP_WHATSAPP_BROADCAST_DELAY || '100', 10),

  /**
   * Business-initiated WhatsApp (customer has not messaged you first): requires a template
   * approved in Meta Business Manager. Set name + language to match the template exactly.
   */
  outreachTemplateName: (process.env.REACT_APP_WHATSAPP_OUTREACH_TEMPLATE_NAME || '').trim(),
  outreachTemplateLanguage: (process.env.REACT_APP_WHATSAPP_OUTREACH_TEMPLATE_LANGUAGE || 'en').trim(),
  /**
   * Body variable count in that template (0–4). Must match Meta: {{1}}… in order:
   * 1=customer name, 2=company, 3=reg, 4=vehicle. Use 1 for a single combined {{1}} line.
   */
  outreachTemplateBodyParamCount: (() => {
    const raw = parseInt(process.env.REACT_APP_WHATSAPP_OUTREACH_TEMPLATE_BODY_PARAMS || '4', 10);
    if (Number.isNaN(raw)) return 4;
    return Math.min(4, Math.max(0, raw));
  })(),

  /**
   * Optional Meta template for manual “service due” WhatsApp (same variable order as automated job when n=5).
   */
  serviceDueTemplateName: (process.env.REACT_APP_SERVICE_DUE_WHATSAPP_TEMPLATE_NAME || '').trim(),
  serviceDueTemplateLanguage: (process.env.REACT_APP_SERVICE_DUE_WHATSAPP_TEMPLATE_LANGUAGE || 'en').trim(),
  serviceDueTemplateBodyParamCount: (() => {
    const raw = parseInt(process.env.REACT_APP_SERVICE_DUE_WHATSAPP_TEMPLATE_BODY_PARAMS || '5', 10);
    if (Number.isNaN(raw)) return 5;
    return Math.min(6, Math.max(0, raw));
  })(),

  /**
   * Meta-approved template for sending repair quotes to customers.
   * Once template has variables, body uses {{1}}–{{6}}: reg, vehicle, summary, cost, description, company.
   * Set REACT_APP_WHATSAPP_QUOTE_TEMPLATE_BODY_PARAMS=0 if the template has no variable placeholders yet.
   */
  quoteTemplateName: (process.env.REACT_APP_WHATSAPP_QUOTE_TEMPLATE_NAME || '').trim(),
  quoteTemplateLanguage: (process.env.REACT_APP_WHATSAPP_QUOTE_TEMPLATE_LANGUAGE || 'en').trim(),
  quoteTemplateBodyParamCount: (() => {
    const raw = parseInt(process.env.REACT_APP_WHATSAPP_QUOTE_TEMPLATE_BODY_PARAMS || '6', 10);
    if (Number.isNaN(raw)) return 6;
    return Math.min(6, Math.max(0, raw));
  })(),

  /**
   * Meta-approved template for MOT due reminders.
   * Body variables in order: {{1}} customer name, {{2}} vehicle reg, {{3}} MOT expiry date, {{4}} company name.
   * Use REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_BODY_PARAMS=0 for a static template with no placeholders.
   */
  motReminderTemplateName: (process.env.REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_NAME || '').trim(),
  motReminderTemplateLanguage: (process.env.REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_LANGUAGE || 'en').trim(),
  motReminderTemplateBodyParamCount: (() => {
    const raw = parseInt(process.env.REACT_APP_MOT_REMINDER_WHATSAPP_TEMPLATE_BODY_PARAMS || '4', 10);
    if (Number.isNaN(raw)) return 4;
    return Math.min(4, Math.max(0, raw));
  })(),

  /**
   * Meta-approved template sent when an MOT is booked in.
   * Body: {{1}} name, {{2}} make+model, {{3}} reg, {{4}} date/time, {{5}} company.
   */
  motBookedConfirmationTemplateName: (process.env.REACT_APP_MOT_BOOKED_WHATSAPP_TEMPLATE_NAME || '').trim(),
  motBookedConfirmationTemplateLanguage: (process.env.REACT_APP_MOT_BOOKED_WHATSAPP_TEMPLATE_LANGUAGE || 'en').trim(),
  motBookedConfirmationTemplateBodyParamCount: (() => {
    const raw = parseInt(process.env.REACT_APP_MOT_BOOKED_WHATSAPP_TEMPLATE_BODY_PARAMS || '5', 10);
    if (Number.isNaN(raw)) return 4;
    return Math.min(5, Math.max(0, raw));
  })(),
};

/**
 * Helper Functions
 */
export const configHelpers = {
  // Format currency amount
  formatCurrency: (amountInPence) => {
    const pounds = (amountInPence / 100).toFixed(2);
    return `${localeConfig.currencySymbol}${pounds}`;
  },
  
  // Get Google favicon URL
  getFaviconUrl: (domain) => {
    return apiConfig.googleFaviconApi.replace('{domain}', domain);
  },
  
  // Check if email is admin domain
  isAdminEmail: (email) => {
    if (!email || typeof email !== 'string') return false;
    return email.toLowerCase().endsWith(companyConfig.adminEmailDomain.toLowerCase());
  },
  
  // Format phone number with country code
  formatPhoneNumber: (phone) => {
    if (!phone) return null;
    
    // Remove all non-digit characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');
    
    // If it doesn't start with +, assume default country code
    if (!cleaned.startsWith('+')) {
      // Remove leading 0 if present
      if (cleaned.startsWith('0')) {
        cleaned = cleaned.substring(1);
      }
      // Add default country code
      cleaned = localeConfig.defaultCountryCode + cleaned;
    }
    
    return cleaned;
  },
};

// Export default configuration object
const appConfig = {
  company: companyConfig,
  firebase: firebaseConfig,
  api: apiConfig,
  stripe: stripeConfig,
  adyen: adyenConfig,
  locale: localeConfig,
  platforms: platformUrls,
  ui: uiConfig,
  whatsapp: whatsappConfig,
  helpers: configHelpers,
};

export default appConfig;

